import { Button } from "@mui/material";
import { ChevronLeft, ClipboardMinus, Pencil } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import axiosInstance from "../../Axios";
import CommunicationDataGrid from "../../components/communicationdatagrid/CommunicationDataGrid";
import EditSchoolDialog from "../../components/EditSchoolDialog.jsx";
import SmartClassesTable from "../../components/smartclassestable/SmartClassesTable";
import StackedAreaChart from "../../components/stackedareachart/StackedAreaChart";
import TeachersTable from "../../components/teacherstable/TeachersTable";
import useAuth from "../../contexts/UserContext.tsx";
import "./school.scss";
import { ScaleLoader } from "react-spinners";
import { useGlobalContext } from "../../contexts/GlobalContext.jsx";

const School = () => {
  const { id } = useParams();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [reload, setReload] = useState(false);
  const [smartClassesOpsData, setSmartClassesOpsData] = useState([]);
  const [schoolKPIData, setSchoolKPIData] = useState();
  const [schoolCommunicationDetails, setSchoolCommunicationDetails] =
    useState();
  const [teacherStatusData, setTeacherStatusData] = useState();
  const [editSchoolDialogOpen, setEditSchoolDialogOpen] = useState(false);
  const [schoolDetails, setSchoolDetails] = useState({});
  const [schoolDetailsLoading, setSchoolDetailsLoading] = useState(false);
  const { yearlySummaryData, setYearlySummaryData } = useGlobalContext();

  const getSchoolKPIDataBySchool = async () => {
    try {
      const res = await axiosInstance.post("getSchoolKPIBySchool", {
        auth_token: user.auth_token,
        school_id: id,
        user_id: user.user_id,
      });
      setSchoolKPIData(res.data.data.school_kpi);
    } catch (err) {
      console.error(err.message);
    }
  };

  const getSmartClassesOpsData = async () => {
    try {
      const res = await axiosInstance.post("getSmartClassOpsStatusBySchool", {
        user_id: user.user_id,
        auth_token: user.auth_token,
        school_id: id,
      });
      if (res.data.data.message === "Invalid User") {
        toast.error("Invalid User");
        logout();
        return;
      }
      setSmartClassesOpsData(res.data.data.smartclass_ops_status);
    } catch (err) {
      toast.error("Error getting class data.");
    }
  };

  const getSchoolDetails = async () => {
    setSchoolDetailsLoading(true);
    axiosInstance
      .post("getSchoolDetailsById", {
        school_id: id,
        user_id: user.user_id,
        auth_token: user.auth_token,
      })
      .then((res) => {
        setSchoolDetails(res.data.data.school_details);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Error getting school details");
      })
      .finally(() => {
        setSchoolDetailsLoading(false);
      });
  };

  const getCommunicationDetails = async () => {
    try {
      const res = await axiosInstance.post("getschoolfollowup", {
        user_id: user.user_id,
        school_id: id,
        auth_token: user.auth_token,
      });
      let data = res.data.data.school_followups;
      data = data.map((comm, idx) => {
        comm = {
          ...comm,
          id: idx,
        };
        return comm;
      });
      setSchoolCommunicationDetails(data);
    } catch (err) {
      console.error(err.message);
    }
  };

  const getTeacherStatusData = async () => {
    try {
      const res = await axiosInstance.post("getTeacherStatus", {
        school_id: id,
        auth_token: user.auth_token,
      });
      setTeacherStatusData(res.data.data.response);
    } catch (err) {
      console.error(err.message);
    }
  };

  const getYearlySummaryData = async () => {
    try {
      const res = await axiosInstance.post(
        "/getSchoolYearlySummaryListByDistrict",
        {
          user_id: user.user_id,
          auth_token: user.auth_token,
          school_types: `'${user.view_access_list.school_types
            .split(",")
            .join("','")}'`,
          district_names: `'${user.view_access_list.district
            .split(",")
            .join("','")}'`,
        }
      );
      const res2 = await axiosInstance.post("/getSchoolSummaryListByDistrict", {
        user_id: user.user_id,
        auth_token: user.auth_token,
        school_types: `'${user.view_access_list.school_types
          .split(",")
          .join("','")}'`,
        district_names: `'${user.view_access_list.district
          .split(",")
          .join("','")}'`,
      });

      let currentSchoolYearlyData =
        res.data.data.school_yearly_summary_list[id];
      let currentSchoolData = res2.data.data.school_summary_list.find(
        (school) => school.school_id === id
      );
      setYearlySummaryData({
        ...currentSchoolData,
        yearly_excs_usage_cnt:
          parseInt(currentSchoolData.excs_usage_cnt) -
          parseInt(currentSchoolYearlyData[0].excs_count),
        yearly_video_usage_cnt:
          parseInt(currentSchoolData.video_usage_cnt) -
          parseInt(currentSchoolYearlyData[0].video_count),
      });
    } catch (err) {
      console.error(err.message);
    }
  };
  useEffect(() => {
    getSmartClassesOpsData();
    // getTeachersBySchools();
    getSchoolKPIDataBySchool();
    getCommunicationDetails();
    getTeacherStatusData();
    getSchoolDetails();
    if (!yearlySummaryData) {
      getYearlySummaryData();
    }

    if (reload) {
      setReload(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reload]);

  return (
    <>
      <div
        className={`flex ${
          user?.view_access_list?.schoolID == null
            ? "justify-between"
            : "justify-end"
        }`}
      >
        {user?.view_access_list?.schoolID == null && (
          <Button
            className="mx-3 flex justify-between"
            onClick={() => navigate("/")}
          >
            <ChevronLeft size={16} />
            Back
          </Button>
        )}
        <Button
          className="mx-3 flex gap-2 justify-between"
          variant="outlined"
          onClick={() => navigate(`/school/${id}/report`)}
        >
          <ClipboardMinus size={16} className="text-[#7974d7]" />
          Generate School Report
        </Button>
      </div>
      <div className="container school p-3 h-auto">
        <div className="grid grid-cols-4 gap-2">
          <div className="schoolCard col-span-1">
            {user.user_type === "admin" && (
              <button
                className="flex justify-center items-center focus:outline-none absolute right-0 top-0 bg-[#dcd9fe] rounded p-3"
                onClick={() => setEditSchoolDialogOpen(true)}
              >
                <Pencil size={18} className="text-[#7974d7]" />
              </button>
            )}

            {schoolDetailsLoading ? (
              <div className="flex justify-center my-auto">
                <ScaleLoader color="#7974d7" />
              </div>
            ) : (
              <>
                <div className="font-sans text-2xl text-[#7974d7] mb-6 w-[90%]">
                  {schoolDetails.school_name}
                </div>

                <div className="details">
                  <div className="detailItem">
                    <span className="itemKey">Headmaster:</span>
                    <span className="itemValue">
                      {schoolDetails && schoolDetails.hm_name}
                    </span>
                  </div>
                  <div className="detailItem">
                    <span className="itemKey">Phone:</span>
                    <span className="itemValue">
                      {schoolDetails && schoolDetails.hm_phone}
                    </span>
                  </div>
                  <div className="detailItem">
                    <span className="itemKey">Address:</span>
                    <span className="itemValue">
                      {schoolDetails && schoolDetails.address1}
                    </span>
                  </div>
                  <div className="detailItem">
                    <span className="itemKey">Smart Classes:</span>
                    <span className="itemValue">
                      {schoolKPIData && schoolKPIData.center_cnt}
                    </span>
                  </div>
                  <div className="detailItem">
                    <span className="itemKey">Teachers:</span>
                    <span className="itemValue">
                      {schoolKPIData && schoolKPIData.teacher_cnt}
                    </span>
                  </div>
                  <div className="detailItem">
                    <span className="itemKey">Current Year Video Usage:</span>
                    {yearlySummaryData ? (
                      <span className="itemValue">
                        {Intl.NumberFormat("en-US", {
                          notation: "compact",
                          compactDisplay: "short",
                        }).format(
                          yearlySummaryData.yearly_video_usage_cnt
                        )}{" "}
                        Minutes
                      </span>
                    ) : (
                      <span className="itemValue">Loading...</span>
                    )}
                  </div>
                  <div className="detailItem">
                    <span className="itemKey">
                      Current Year Exercise Usage:
                    </span>
                    {yearlySummaryData ? (
                      <span className="itemValue">
                        {yearlySummaryData &&
                          Intl.NumberFormat("en-US", {
                            notation: "compact",
                            compactDisplay: "short",
                          }).format(
                            yearlySummaryData.yearly_excs_usage_cnt
                          )}{" "}
                        Minutes
                      </span>
                    ) : (
                      <span className="itemValue">Loading...</span>
                    )}
                  </div>
                  <div className="detailItem">
                    <span className="itemKey">Total Video Usage:</span>
                    <span className="itemValue">
                      {schoolKPIData &&
                        Intl.NumberFormat("en-US", {
                          notation: "compact",
                          compactDisplay: "short",
                        }).format(schoolKPIData.video_usage_cnt)}{" "}
                      Minutes
                    </span>
                  </div>
                  <div className="detailItem">
                    <span className="itemKey">Total Exercise Usage:</span>
                    <span className="itemValue">
                      {schoolKPIData &&
                        Intl.NumberFormat("en-US", {
                          notation: "compact",
                          compactDisplay: "short",
                        }).format(schoolKPIData.excs_usage_cnt)}{" "}
                      Minutes
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
          <div className="col-span-3">
            <StackedAreaChart
              schoolID={id}
              title="Smart Class Usage"
              aspect={6 / 3}
            />
          </div>
        </div>
        <SmartClassesTable
          data={smartClassesOpsData}
          schoolData={schoolDetails}
          setReload={setReload}
        />
        <TeachersTable
          setReload={setReload}
          schoolName={schoolDetails.school_name}
          schoolID={id}
          data={teacherStatusData}
        />
        <CommunicationDataGrid
          schoolName={schoolDetails.school_name}
          data={schoolCommunicationDetails}
        />
      </div>
      {schoolDetails && schoolDetails.school_name && (
        <EditSchoolDialog
          open={editSchoolDialogOpen}
          setEditSchoolDialogOpen={setEditSchoolDialogOpen}
          setReload={setReload}
          schoolDetails={{
            schoolName: schoolDetails.school_name,
            school_id: id,
            hmName: schoolDetails.hm_name,
            hmPhone: schoolDetails.hm_phone,
            address: schoolDetails.address1,
          }}
        />
      )}
    </>
  );
};

export default School;
